import {
  DotLottieWorker,
  DotLottieWorkerReact,
} from '@lottiefiles/dotlottie-react'
import { useAudio } from '@providers'
import { motion, usePresence } from 'framer-motion'
import { useEffect, useState, useRef, ComponentProps } from 'react'

import { webApp } from '@shared'

const fadeInVariants = {
  initial: {
    opacity: 0,
    transition: {
      duration: 0,
    },
  },
  enter: {
    opacity: 1,
  },
  exit: {
    opacity: 0.9,
    transition: {
      duration: 1,
    },
  },
}

const renderConfig = {
  freezeOnOffscreen: false,
}

const motionProps = {
  initial: 'initial',
  animate: 'enter',
  variants: fadeInVariants,
}

interface StarAnimationProps extends ComponentProps<typeof motion.div> {
  starLottieData: any
  delay: number
  onDisappear?: () => void
  disabled?: boolean
  onClick?: () => void
  shouldAnimate?: boolean
  isVisible?: boolean
  isActive?: boolean
}

export function StarAnimation({
  starLottieData,
  delay,
  onDisappear = () => {},
  onClick = () => {},
  disabled = false,
  shouldAnimate = true,
  isVisible = true,
  isActive = false,
  ...rest
}: StarAnimationProps) {
  const [isPresent, safeToRemove] = usePresence()
  const [isLoaded, setIsLoaded] = useState(false)
  const [dotLottie, setDotLottie] = useState<DotLottieWorker | null>(null)
  const [isCompleted, setIsCompleted] = useState(false)

  const animationTimerRef = useRef<NodeJS.Timeout | undefined>(undefined)
  const completionTimerRef = useRef<NodeJS.Timeout | undefined>(undefined)

  const { playStar } = useAudio()

  const cleanup = (): void => {
    if (animationTimerRef.current) {
      clearTimeout(animationTimerRef.current)
    }
    if (completionTimerRef.current) {
      clearTimeout(completionTimerRef.current)
    }
    if (dotLottie) {
      dotLottie.stop()
    }
  }

  useEffect(() => {
    const handleLoad = () => {
      setIsLoaded(true)
    }
    dotLottie?.addEventListener('render', handleLoad)

    return () => {
      cleanup()
      dotLottie?.removeEventListener('render', handleLoad)
    }
  }, [dotLottie, shouldAnimate])

  useEffect(() => {
    if (dotLottie) {
      if (shouldAnimate) {
        setTimeout(() => {
          cleanup()
          dotLottie.setSegment(0, 238)
          dotLottie.play()
        }, 100)
      } else {
        dotLottie.stop()
      }
    }
  }, [dotLottie, shouldAnimate])

  const handleClick = () => {
    if (!isLoaded || disabled) {
      return
    }

    onClick()
    setIsCompleted(true)

    if (isCompleted || !dotLottie) return

    dotLottie.setLoop(false).then(() => {
      dotLottie.setSegment(238, 300)
      webApp.HapticFeedback.impactOccurred('soft')
      setIsCompleted(true)
      playStar()

      completionTimerRef.current = setTimeout(async () => {
        dotLottie.play()

        dotLottie.addEventListener('complete', () => {
          onDisappear()
          !isPresent && safeToRemove()
        })
      }, 100)
    })
  }
  useEffect(() => {
    if (!isPresent && !isCompleted) {
      safeToRemove()
    }
  }, [isPresent, isCompleted])

  const framerMotionDelay = delay / 1000

  const getAnimation = () => {
    if (isCompleted) return 'exit'
    if (!isPresent) return 'initial'
    return 'enter'
  }

  useEffect(() => {
    if (!isVisible) {
      return
    }
    setTimeout(() => {
      dotLottie?.resize()
    }, 100)
  }, [isVisible])

  useEffect(() => {
    if (!isActive) {
      return
    }
    setTimeout(() => {
      dotLottie?.resize()
    }, 100)
  }, [isActive])

  return (
    <motion.div
      {...motionProps}
      onClick={handleClick}
      transition={{
        delay: framerMotionDelay,
        ease: 'easeInOut',
        duration: 0.35,
      }}
      animate={getAnimation()}
      {...rest}
    >
      <DotLottieWorkerReact
        dotLottieRefCallback={setDotLottie}
        data={starLottieData}
        autoplay={false}
        renderConfig={renderConfig}
        loop
      />
    </motion.div>
  )
}
